import { useCallback, useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useBrainApi } from "../../api/brainApi";
import { Domain, WikiDoc } from "../../api/types";
import { AlertBanner, PageTitle, Panel } from "../../components/controlPlaneUi";

// Strip YAML frontmatter (--- ... ---) before rendering so it doesn't appear as text.
function stripFrontmatter(content: string): string {
  if (!content.startsWith("---")) return content;
  const end = content.indexOf("\n---", 3);
  return end < 0 ? content : content.slice(end + 4).trimStart();
}

function groupBySection(docs: WikiDoc[]): Record<string, WikiDoc[]> {
  const map: Record<string, WikiDoc[]> = {};
  for (const doc of docs) {
    (map[doc.section] ??= []).push(doc);
  }
  return map;
}

const SECTION_LABELS: Record<string, string> = {
  general: "General",
  codebase: "Codebase",
  entities: "Entities",
  concepts: "Concepts",
  projects: "Projects",
  meetings: "Meetings",
  gaps: "Gaps",
  requirements: "Requirements",
};

const DocsPage: React.FC = () => {
  const api = useBrainApi();

  const [domains, setDomains] = useState<Domain[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>("");

  const [docs, setDocs] = useState<WikiDoc[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);

  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);

  // Load domain list once.
  useEffect(() => {
    api.listDomains().then((d) => {
      setDomains(d);
      if (d.length > 0) setSelectedDomain(d[0].name);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load wiki docs when domain changes.
  const loadDocs = useCallback(async (domain: string) => {
    if (!domain) return;
    setDocsLoading(true);
    setDocsError(null);
    setDocs([]);
    setSelectedPath(null);
    setContent(null);
    try {
      setDocs(await api.listWikiDocs(domain));
    } catch (e) {
      setDocsError(e instanceof Error ? e.message : "failed to load docs");
    } finally {
      setDocsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (selectedDomain) void loadDocs(selectedDomain);
  }, [selectedDomain, loadDocs]);

  // Load doc content when selection changes.
  useEffect(() => {
    if (!selectedPath || !selectedDomain) return;
    setContentLoading(true);
    setContentError(null);
    setContent(null);
    api.readWikiDoc(selectedDomain, selectedPath)
      .then((text) => setContent(text))
      .catch((e) => setContentError(e instanceof Error ? e.message : "failed to load file"))
      .finally(() => setContentLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPath, selectedDomain]);

  const grouped = useMemo(() => groupBySection(docs), [docs]);
  const sections = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  const selectedDoc = useMemo(
    () => docs.find((d) => d.path === selectedPath) ?? null,
    [docs, selectedPath]
  );

  const bodyContent = stripFrontmatter(content ?? "");

  return (
    <div>
      <PageTitle kind="Knowledge" name="docs" />

      {/* Domain selector */}
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm text-slate-600 font-medium">Domain</label>
        <select
          value={selectedDomain}
          onChange={(e) => setSelectedDomain(e.target.value)}
          className="text-sm border border-[#262420] px-2 py-1 bg-[#1a1917] text-[#E5E0D5] focus:outline-none focus:ring-1 focus:ring-[#C9A260] cp-select"
        >
          {domains.length === 0 && <option value="">No domains</option>}
          {domains.map((d) => (
            <option key={d.id} value={d.name}>{d.name}</option>
          ))}
        </select>
      </div>

      {docsError && <AlertBanner variant="error">{docsError}</AlertBanner>}

      <div className="flex gap-4 min-h-0">
        {/* Sidebar: file tree */}
        <div className="w-52 flex-shrink-0">
          <Panel title="Files">
            {docsLoading ? (
              <p className="text-xs text-slate-500">Loading…</p>
            ) : docs.length === 0 && !docsLoading && selectedDomain ? (
              <p className="text-xs text-slate-500">No docs yet — run a Brain ingestion first.</p>
            ) : (
              <div className="space-y-3">
                {sections.map((section) => (
                  <div key={section}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                      {SECTION_LABELS[section] ?? section}
                    </div>
                    <ul className="space-y-0.5">
                      {grouped[section].map((doc) => (
                        <li key={doc.path}>
                          <button
                            onClick={() => setSelectedPath(doc.path)}
                            className={`w-full text-left text-xs px-2 py-1 rounded-sm truncate transition-colors ${
                              selectedPath === doc.path
                                ? "bg-[#C9A260] text-[#111110]"
                                : "text-[#9D9585] hover:bg-white/4 hover:text-[#E5E0D5]"
                            }`}
                            title={doc.path}
                          >
                            {doc.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>

        {/* Main: markdown content */}
        <div className="flex-1 min-w-0">
          <Panel title={selectedDoc ? `${selectedDoc.section} / ${selectedDoc.name}` : "Select a document"}>
            {contentError && <AlertBanner variant="error">{contentError}</AlertBanner>}
            {contentLoading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : content === null ? (
              <p className="text-sm text-slate-400">
                {selectedPath ? "" : "Choose a document from the list."}
              </p>
            ) : (
              <div className="prose-sm max-w-none text-slate-800">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-xl font-bold mt-6 mb-3 text-[#E5E0D5] first:mt-0">{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-lg font-semibold mt-5 mb-2 text-slate-800">{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-base font-semibold mt-4 mb-1 text-slate-700">{children}</h3>
                    ),
                    p: ({ children }) => (
                      <p className="mb-3 text-sm text-slate-700 leading-relaxed">{children}</p>
                    ),
                    ul: ({ children }) => (
                      <ul className="mb-3 pl-5 list-disc text-sm text-slate-700 space-y-1">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="mb-3 pl-5 list-decimal text-sm text-slate-700 space-y-1">{children}</ol>
                    ),
                    li: ({ children }) => <li className="text-sm">{children}</li>,
                    pre: ({ children }) => (
                      <pre className="mb-3 bg-[#141210] border border-[#262420] p-3 overflow-x-auto text-xs font-mono text-[#C9A260]/80">
                        {children}
                      </pre>
                    ),
                    code: ({ children, className }) =>
                      className ? (
                        <code className="text-xs font-mono">{children}</code>
                      ) : (
                        <code className="bg-[#262420] px-1.5 py-0.5 rounded text-xs font-mono text-[#C9A260]/80">
                          {children}
                        </code>
                      ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-[#262420] pl-4 my-3 italic text-[#9D9585] text-sm">
                        {children}
                      </blockquote>
                    ),
                    a: ({ href, children }) => (
                      <a href={href} className="text-[#C9A260] hover:text-[#E5C88A] hover:underline text-sm" target="_blank" rel="noreferrer">
                        {children}
                      </a>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold text-[#E5E0D5]">{children}</strong>
                    ),
                    hr: () => <hr className="my-4 border-slate-200" />,
                    table: ({ children }) => (
                      <div className="overflow-x-auto mb-3">
                        <table className="text-xs border-collapse w-full">{children}</table>
                      </div>
                    ),
                    th: ({ children }) => (
                      <th className="border border-[#262420] px-3 py-2 bg-[#141210] font-semibold text-left text-xs text-[#5A5448]">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="border border-[#262420] px-3 py-2 text-xs text-[#9D9585]">{children}</td>
                    ),
                  }}
                >
                  {bodyContent}
                </ReactMarkdown>
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default DocsPage;
